import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { formatNumber } from 'utils/formats';

import InputDate from 'modules/_shared/components/InputDate';
import InputRadio from 'modules/_shared/components/InputRadio';
import InputNumber from 'modules/_shared/components/InputNumber';
import InputCheckbox from 'modules/_shared/components/InputCheckbox';
import InputTextArea from 'modules/_shared/components/InputTextArea';
import MandatoryField from 'modules/_shared/components/MandatoryField';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import PartnerAvatarInfo from 'modules/_shared/components/PartnerAvatarInfo';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';

import { IncentiveOfferType } from 'modules/incentivesMarket/types';
import { BLANK_PROFILE_PICTURE } from 'constants/defaultConstants';
import { useAddIncentiveOfferPage } from '../context/AddIncentiveOfferPageContext';
import { addIncentiveOfferLabels } from '../constants/addIncentiveOfferLabels';
import BuyOfferUnitsSection from './BuyOfferUnitsSection';
import SellerLimitsControls from './SellerLimitsControls';

const VALIDITY_PERIOD_DAYS = [7, 15, 30, 60, 90];

const AddIncentiveOfferDataSection: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useAddIncentiveOfferPage();

  const labels = useMemo(
    () =>
      addIncentiveOfferLabels[data.incentiveOfferType as IncentiveOfferType],
    [data.incentiveOfferType]
  );

  const isSellOffer = useMemo(
    () => data.incentiveOfferType === IncentiveOfferType.SELL,
    [data.incentiveOfferType]
  );

  const isBuyOffer = useMemo(
    () => data.incentiveOfferType === IncentiveOfferType.BUY,
    [data.incentiveOfferType]
  );

  const partialAcceptanceTooltipKey = isSellOffer
    ? 'IncentivesMarketIAllowPartialAcceptanceTooltip_SELL'
    : 'IncentivesMarketIAllowPartialAcceptanceTooltip_BuyOffer';

  const minUnitsPerAcceptanceNoteKey = isSellOffer
    ? 'IncentivesMarketMinUnitsPerAcceptanceNote'
    : 'IncentivesMarketMinUnitsPerAcceptanceNote_BuyOffer';

  const unitsAfterSale = isSellOffer
    ? Math.max(0, data.beneficiaryCurrentUnitsCount - data.sharesToTransfer)
    : 0;

  const hasMultiplePlans = data.plansBreakdown.length > 1;

  const availablePlanTypes = useMemo(
    () =>
      Array.from(
        new Set(
          data.plansBreakdown
            .map((plan) => plan.planType)
            .filter((planType): planType is string => Boolean(planType))
        )
      ),
    [data.plansBreakdown]
  );

  const selectedValidityDays = useMemo(() => {
    if (!data.startDate || !data.endDate) return null;
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    const diffDays = Math.round(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );
    return VALIDITY_PERIOD_DAYS.includes(diffDays) ? diffDays : null;
  }, [data.startDate, data.endDate]);

  return (
    <div className="col-lg-7">
      {/* Card 1: IncentivesMarketBeneficiary */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-user text-primary mr-2" />
            {t(labels.SELECT_PARTNER)}
          </h6>

          {isSellOffer && (
            <div className="row  mb-3">
              <div className="col-12 col-lg-5">
                <PartnerAvatarInfo
                  id={data.selectedBeneficiaryOption?.value || ''}
                  name={
                    data.selectedBeneficiaryOption?.name ||
                    data.selectedBeneficiaryOption?.label ||
                    '-'
                  }
                  email={data.selectedBeneficiaryOption?.email || '-'}
                  image={
                    data.selectedBeneficiaryOption?.image ||
                    BLANK_PROFILE_PICTURE
                  }
                  societyId={data.societyId}
                  haveStatus={false}
                  haveLink={false}
                />
                {data.isBeneficiaryView && !data.selectedBeneficiaryOption && (
                  <div className="text-soft">{t('LoadingBeneficiaries')}</div>
                )}
              </div>

              <div className="col-6 col-lg-3 d-flex flex-column">
                <span className="text-soft">
                  {t('IncentivesMarketCurrentUnits')}
                </span>
                <span className="text-left">
                  {data.isLoadingBeneficiaryContext ? (
                    <span className="text-soft">{t('Loading')}</span>
                  ) : (
                    formatNumber(data.beneficiaryCurrentUnitsCount)
                  )}
                </span>
                {hasMultiplePlans && data.beneficiaryCif && (
                  <span className="text-soft fs-12px">
                    {t('IncentivesMarketUnitsAcrossPlansByCif')}
                  </span>
                )}
              </div>

              <div className="col-6 col-lg-4 d-flex flex-column">
                <span className="text-soft">
                  {t('IncentivesMarketUnitsAfterSale')}
                </span>
                <span className="text-left text-primary">
                  {formatNumber(unitsAfterSale)}
                </span>
              </div>
            </div>
          )}

          {data.plansBreakdown.length > 0 && (
            <div className="row mb-2 px-2">
              <div className="col-12 alert alert-light">
                <span className="d-block mb-2 fs-14px fw-bold">
                  {t('IncentivesMarketPlansBreakdown')}
                </span>
                <ul className="list list-sm mb-0 fs-13px">
                  {data.plansBreakdown.map((plan) => (
                    <li
                      key={plan.beneficiaryId}
                      className="list-item pb-1 w-100"
                    >
                      <span>{plan.planName || t('Plan')}</span>
                      <span className="text-secondary ml-2">
                        {formatNumber(plan.availableUnits)}{' '}
                        {t('IncentivesMarketUnitsLower')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {data.plansBreakdown.length === 0 && isSellOffer && (
            <div className="row mb-4 px-2">
              <div className="col-12 alert alert-warning">
                <em className="icon ni ni-alert-circle text-warning mr-2" />
                <span className="lh-14px">
                  {t('IncentivesMarketNoUnitsAvailable')}
                </span>
              </div>
            </div>
          )}

          {isSellOffer && data.reservedUnits > 0 && (
            <div className="row mb-3">
              <div className="col-12">
                <span className="text-soft fs-13px">
                  {t('IncentivesMarketReservedUnitsByCif', {
                    count: formatNumber(data.reservedUnits),
                  })}
                </span>
              </div>
            </div>
          )}

          {data.isBeneficiaryView && !isSellOffer && (
            <div className="form-group mb-0">
              <div className="form-control-wrap">
                {data.selectedBeneficiaryOption ? (
                  <PartnerAvatarInfo
                    id={data.selectedBeneficiaryOption.value}
                    name={
                      data.selectedBeneficiaryOption.name ||
                      data.selectedBeneficiaryOption.label
                    }
                    email={data.selectedBeneficiaryOption.email || ''}
                    image={data.selectedBeneficiaryOption.image}
                    societyId={data.societyId}
                    haveStatus={false}
                    haveLink={false}
                  />
                ) : (
                  <div className="text-soft">{t('LoadingBeneficiaries')}</div>
                )}
              </div>
            </div>
          )}

          {!data.isBeneficiaryView && (
            <div className="form-group mb-0">
              <label className="form-label" htmlFor="partner-select-input">
                {t(labels.SELECT_PARTNER)} <MandatoryField />
              </label>
              <div className="form-control-wrap">
                <CustomAsyncSelect
                  id="partner-select-input"
                  value={data.selectedBeneficiaryOption}
                  onChange={actions.handleChangePartner}
                  onFetchData={actions.handleSearchBeneficiaryByTerm}
                  placeholder={t('SearchBeneficiary')}
                  minimumInputLength={3}
                  noDataLabelKey="IncentivesMarketNoBeneficiariesFound"
                  loadingLabelKey="LoadingBeneficiaries"
                  errorLabelKey="ErrorLoadingBeneficiaries"
                  defaultOptions={data.beneficiaryOptions}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card 2: Shares */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-layers text-primary mr-2" />
            {t('IncentivesMarketUnits')}
          </h6>

          {isSellOffer ? (
            <>
              <div className="row">
                <div className="col-md-6 mb-4">
                  <InputNumber
                    id="shares-to-transfer-input"
                    label={t(labels.SHARES_TO_TRANSFER)}
                    value={data.sharesToTransfer}
                    decimalScale={2}
                    max={
                      Number.isFinite(data.maxSharesToTransfer)
                        ? data.maxSharesToTransfer
                        : undefined
                    }
                    onChange={actions.handleChangeSharesToTransfer}
                    required
                  />
                </div>

                <div className="col-md-6 mb-4 d-flex align-items-end">
                  <InputCheckbox
                    id="sell-all-my-shares-checkbox"
                    label={t('IncentivesMarketSellAllMyUnits')}
                    checked={data.isSellAllMyShares}
                    onChange={actions.handleChangeIsSellAllMyShares}
                    disabled={
                      !data.selectedBeneficiaryOption ||
                      data.maxSharesToTransfer <= 0 ||
                      data.isLoadingBeneficiaryContext
                    }
                    marginBottom={0}
                  />
                </div>
              </div>

              <hr />

              <SellerLimitsControls
                variant="SELL"
                sellerParticipationLimits={data.sellerParticipationLimits}
                sharesToTransfer={data.sharesToTransfer}
                unitPrice={data.pricePerShare}
                availablePlanTypes={availablePlanTypes}
                onChangeAllowSellAll={actions.handleChangeAllowSellAll}
                onChangeSellerLimit={actions.handleChangeSellerLimit}
              />
            </>
          ) : (
            <BuyOfferUnitsSection
              sharesToTransfer={data.sharesToTransfer}
              sharesToTransferLabel={t(labels.SHARES_TO_TRANSFER)}
              sharesToTransferInfo={t(labels.SHARES_TO_TRANSFER_INFO)}
              sellerParticipationLimits={data.sellerParticipationLimits}
              unitPrice={data.pricePerShare}
              onChangeSharesToTransfer={actions.handleChangeSharesToTransfer}
              onChangeAllowSellAll={actions.handleChangeAllowSellAll}
              onChangeSellerLimit={actions.handleChangeSellerLimit}
            />
          )}
        </div>
      </div>

      {/* Card 3: Price */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-coin text-primary mr-2" />
            {t('Price')}
          </h6>

          <div className="row ">
            <div className="col-md-6 mb-4">
              <InputNumber
                id="price"
                label={
                  data.isPricePerShare ? t(labels.PRICE) : t(labels.PRICE_TOTAL)
                }
                value={data.price}
                iconRight="icon ni ni-sign-euro"
                decimalScale={2}
                onChange={actions.handleChangePrice}
                required
              />
            </div>

            <div className="col-md-6 mb-4">
              <InputNumber
                id="total-offer-price"
                label={t('EstimatedTotalAmount')}
                value={data.totalOfferPrice}
                iconRight="icon ni ni-sign-euro"
                decimalScale={2}
                onChange={() => {}}
                disabled
              />
            </div>

            {isSellOffer && (
              <>
                <div className="col-md-6 d-flex align-items-center">
                  <InputRadio
                    id="price-per-share-radio"
                    checked={data.isPricePerShare}
                    label={t('IncentivesMarketPricePerUnit')}
                    description={t(labels.PRICE_PER_SHARE_INFO)}
                    onChange={actions.handleChangeIsPricePerShare}
                  />
                </div>

                <div className="col-md-6 d-flex align-items-center">
                  <InputRadio
                    id="total-offer-price-radio"
                    checked={!data.isPricePerShare}
                    label={t('TotalOfferPrice')}
                    description={t(labels.PRICE_TOTAL_INFO)}
                    onChange={actions.handleChangeIsPricePerShare}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Card 4: Validity */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-calendar text-primary mr-2" />
            {t('IncentiveOfferValidityPeriod')}
          </h6>

          <div className="row align-items-end">
            <div className={`${isBuyOffer ? 'col-md-4' : 'col-md-6'} mb-4`}>
              <InputDate
                id="startDate"
                label={t('StartDate')}
                value={data.startDate}
                onChange={actions.handleChangeStartDate}
                required
                lgSize
              />
            </div>

            <div className={`${isBuyOffer ? 'col-md-4' : 'col-md-6'} mb-4`}>
              <InputDate
                id="endDate"
                label={t('EndDate')}
                value={data.endDate}
                onChange={actions.handleChangeEndDate}
                minDate={data.startDate || undefined}
                required
                lgSize
              />
            </div>

            {isBuyOffer && (
              <div className="col-md-4 mb-4">
                <InputDate
                  id="calculationDate"
                  label={t('EligibleCalculationDate')}
                  value={data.calculationDate}
                  onChange={actions.handleChangeCalculationDate}
                  required
                  lgSize
                />
              </div>
            )}

            <div className="col-12">
              <div className="d-flex w-100" style={{ gap: '8px' }}>
                {VALIDITY_PERIOD_DAYS.map((days) => (
                  <Button
                    key={days}
                    className="flex-fill"
                    onClick={() => actions.handleSetDatePreset(days)}
                    variant={
                      selectedValidityDays === days
                        ? ButtonVariants.PRIMARY
                        : ButtonVariants.LIGHT
                    }
                  >
                    <span className="text-center">
                      {t('NDays', { count: days })}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card 5: Advanced Options */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-setting text-primary mr-2" />
            {t('AdvancedOptions')}
          </h6>
          <div className="row">
            <div className="col-md-6 mb-4">
              <InputCheckbox
                id="permitir-parcial-checkbox"
                label={t('IAllowPartialAcceptance')}
                checked={data.isPartialAmountEnabled}
                onChange={actions.handleChangeIsPartialAmountEnabled}
                tooltip={t(partialAcceptanceTooltipKey)}
                marginBottom={0}
              />
            </div>

            {data.isPartialAmountEnabled && (
              <div className="col-md-6" id="minimoContainer">
                <InputNumber
                  id="minimo-acciones-input"
                  label={t('IncentivesMarketMinUnitsPerAcceptance')}
                  value={data.minUnitsPerAcceptance}
                  decimalScale={0}
                  onChange={actions.handleChangeMinSharesPerAcceptance}
                  infoLabel={t(minUnitsPerAcceptanceNoteKey)}
                />
              </div>
            )}
            <div className="col-12">
              <InputTextArea
                id="notas-input"
                label={`${t('AdditionalNotes')} (${t('Optional')})`}
                description={t('VisibleForAllBeneficiaries')}
                value={data.additionalNotes}
                onChange={actions.handleChangeAdditionalNotes}
                placeholder={t('AdditionalNotesPlaceholder')}
                rows={3}
                showCounter={false}
              />
            </div>
          </div>
        </div>
      </div>

      {isSellOffer && data.isLoadingBeneficiaryContext && <CustomLoading />}
    </div>
  );
};

export default AddIncentiveOfferDataSection;
